import { useCallback, useMemo, useState, type MouseEvent, } from "react";
import { useTranslation } from "react-i18next";
import { useMutation, useQuery } from "@tanstack/react-query";
import moment from "moment-timezone";
import { omit } from "lodash";
import { toast } from "sonner";
import { Inbox, Plus, PlusCircle } from "lucide-react";
import Button from "../../../../../components/shared/Button";
import Spinner from "../../../../../components/shared/Spinner";
import ResourceSelect from "../../../../../components/shared/inputs/ResourceSelect";
import { useScheduleContext } from "../../hooks/context/useScheduleContext";
import { useDateFilters } from "../../../../../components/shared/filters/DateFilters/context/useDateFilters";
import { useGroups } from "../../../../Settings/General/OrganisationLevelGroups/context";
import { useFilters } from "../../../../../utils/hooks/useFilters";
import { usePermissions } from "../../hooks/usePermissions";
import { useCompanyTimeFormat } from "../../../../../utils/hooks/company";
import useApi from "../../../../../utils/api";
import { getFormFormattedValues } from "../../helpers/form/getFormFormattedValues";
import { getOrderedWeekdays } from "../../helpers/getOrderedWeekdays";
import { renderUserName } from "../../../../../utils/helpers/user";
import { combineDateAndTime } from "../../../../../utils/helpers/date";
import { firstToUpper } from "../../../../../utils/helpers/string";
import { withLoadingToast } from "../../helpers/withLoadingToast";
import { formatTimeSheetParams } from "../../../../TimeSheet/api/helpers";
import type { Template } from "../../types/ScheduleState";
import type { Event, LevelKey } from "../../types/Event";
import type { Resource } from "../../types/Resource";

interface Params {
    pagination: boolean;
    day: string;
    employeeNumber: string;
}

export interface SelectedEvent {
    user?: Resource;
    startDate?: string;
    date?: string;
}

function AddEventFromTemplate({ selected, closeMenu }: { closeMenu: () => void, selected: SelectedEvent }) {
    const { t } = useTranslation();
    const timeFormat = useCompanyTimeFormat();
    const [filters] = useFilters();
    const timezone = moment.tz.guess();
    const [template, setTemplate] = useState<Template>();
    const { authGet, authPost } = useApi();
    const { dispatch, type, resetCount, refetchEventStatistics } = useScheduleContext();
    const { allocatedGroups } = useGroups();
    const { mode } = useDateFilters();
    const isTeamScheduler = type === "team";
    const orderedWeekdays = getOrderedWeekdays();
    const { canCreate } = usePermissions();
    const resourceUrl = useMemo(() => {
        switch (type) {
            case "profile":
            case "my-profile":
            case "template":
            default: {
                return "/schedule-templates"
            }
        }
    }, [type]);

    const url = useMemo(() => {
        // return isTeamScheduler
        //     ? `/shift-templates/v2/${templateId}`
        //     : `/my/shift-templates/v2/${templateId}`;
        return `/shift-templates/v2/${template?.id}`;
    }, [template?.id]);

    const params = useMemo(() => {
        let values: Params = {
            //TODO: remove pagination
            // page: 0,
            // perPage: 10000000,
            pagination: false,
            day: orderedWeekdays[moment(selected?.startDate).weekday()],
            employeeNumber: selected?.user?.employeeNumber as string,
            ...formatTimeSheetParams({ from: undefined, to: undefined, filters: filters?.filters || [] })
        }
        //     values.userId = selected?.user?.id;
        return values;
    }, [orderedWeekdays, selected?.startDate, selected?.user?.employeeNumber, filters?.filters]);

    const query = useQuery({
        queryKey: [url, params],
        queryFn: ({ queryKey }: any) => {
            const [endpoint, _params] = queryKey;
            return authGet(endpoint, { params: _params })
        },
        enabled: !!template?.id
    });

    const add = useCallback(() => {
        dispatch({
            type: "selected-cell-or-event",
            payload: {
                ...selected,
                // date: orderedWeekdays[moment(selected?.date).weekday()],//moment(selected?.date).format("dddd").toLowerCase(),
                isCreating: true,
                isTemplate: true
            }
        });
        closeMenu();
    }, [dispatch, closeMenu, selected]);

    const levels = useMemo(() => {
        return allocatedGroups?.map((group: any) => group.level.replaceAll("_", ""));
    }, [allocatedGroups]);

    const saveEventMutation = useMutation({
        ...withLoadingToast(t("saving")),
        mutationFn: (values: any) => {
            const startDate = combineDateAndTime(moment(selected?.date), moment.parseZone(values?.startDate));
            let endDate = combineDateAndTime(moment(selected?.date), moment.parseZone(values?.endDate));
            if (moment(endDate).isSameOrBefore(moment(startDate), "seconds")) {
                endDate = moment(endDate).add(1, "day");
            }

            const data = getFormFormattedValues({
                ...values,
                status: "pending",
                startDate,
                endDate,
                date: moment(selected?.date).format("YYYY-MM-DD")
            });
            const url = isTeamScheduler ? "/schedules/v2" : "/my/schedules/v2"
            return authPost(url, { data: omit(data, ["id"]) });
        },
        onSuccess: (response: any) => {
            resetCount?.();
            refetchEventStatistics();
            const type = filters?.mode === "month" ? "create-calendar-event" : "create-event";

            dispatch({
                type,
                payload: {
                    ...response,
                    resourceId: response.user?.id ?? response.user,
                    startDate: moment.parseZone(response.startDate).tz(timezone, true),
                    endDate: moment.parseZone(response.endDate).tz(timezone, true),
                    date: moment.parseZone(response.date).format("YYYY-MM-DD")
                }
            });
            toast.success(t("event-created-successfully"));
        },
        onError: (error: any) => {
            toast.error(t(typeof error === "string" ? error : "something-went-wrong"));
        }
    });

    const handleDoubleClick = useCallback((e: MouseEvent<HTMLDivElement>, event: Event) => {
        e.preventDefault();
        e.stopPropagation();
        const data = getFormFormattedValues({
            ...event,
            date: moment(selected?.date).format("YYYY-MM-DD")
        });
        dispatch({
            type: "selected-cell-or-event",
            payload: {
                ...data,
                date: moment(selected?.date).format("dddd").toLowerCase(),
                isTemplate: true,
                isCreating: false,
                startDate: moment.parseZone(event.startDate).tz(timezone, true),
                endDate: moment.parseZone(event.endDate).tz(timezone, true)
            }
        });
        closeMenu();
    }, [selected?.date, dispatch, timezone, closeMenu]);

    return (
        <div className="tw-p-2">
            <div className="tw-flex tw-items-center tw-justify-between tw-mb-3">
                <h4 className="tw-font-bold tw-w-full">
                    {t("select-template")}
                </h4>

                <Button variant="ghost" className="tw-text-primary" onClick={add}>
                    <Plus size={17} /> {t("add")}
                </Button>
            </div>

            <div>
                <div className="tw-mb-3">
                    <ResourceSelect
                        resourcePath={resourceUrl}
                        mode="single"
                        placeholder={firstToUpper(t("template"))}
                        labelPropName="name"
                        value={template}
                        onChange={(value?: Template) => setTemplate(value)}
                    />
                </div>

                {query.isLoading ? (
                    <div className="tw-w-full tw-flex tw-items-center tw-justify-center tw-py-3">
                        <Spinner className="tw-text-primary" />
                    </div>
                ) : query.data?.data?.events?.length > 0 ? (
                    <div className="tw-flex tw-flex-col tw-max-h-[300px] tw-overflow-y-auto">
                        {query.data?.data?.events?.map((event: Event) => {
                            const firstSection = event?.sections?.[0];

                            return (
                                <div
                                    key={event?.id}
                                    className="tw-rounded-sm tw-mb-1 tw-p-3 tw-bg-primary/20 tw-flex tw-justify-between tw-items-center"
                                    onDoubleClick={(e: MouseEvent<HTMLDivElement>) => handleDoubleClick(e, event)}
                                >
                                    <div className="tw-flex tw-flex-col">
                                        {mode === "month" && <p className="tw-text-sm tw-font-semibold tw-truncate tw-mb-1">{renderUserName(event?.user)}</p>}
                                        <div className="tw-flex tw-gap-1 tw-text-sm tw-font-bold tw-mb-1">
                                            <span>{moment.parseZone(event?.startDate).format(timeFormat)}</span>
                                            {" - "}
                                            <span>{moment.parseZone(event?.endDate).format(timeFormat)}</span>
                                        </div>
                                        <div className="tw-text-sm tw-text-neutral-800">
                                            <span>{firstSection?.payCode?.code}</span>
                                            {levels?.map((level: LevelKey, i: number) => {
                                                const value = firstSection?.[level]?.code;
                                                const nextValue = firstSection?.[levels?.[i + 1] as LevelKey];
                                                return (
                                                    <>
                                                        {i === 0 && " • "}
                                                        <span key={i}>
                                                            {value}
                                                        </span>
                                                        {nextValue && " • "}
                                                    </>
                                                )
                                            })}
                                        </div>
                                    </div>

                                    {canCreate && (
                                        <PlusCircle
                                            size={20}
                                            strokeWidth={3}
                                            className="tw-text-primary tw-cursor-pointer"
                                            onClick={(e) => {
                                                e.preventDefault();
                                                e.stopPropagation();
                                                saveEventMutation.mutate(event);
                                            }}
                                        />
                                    )}
                                </div>
                            );
                        })}
                    </div>
                ) : (
                    <div className="tw-border tw-rounded-sm tw-p-4 tw-text-neutral-400 tw-w-full tw-flex tw-flex-col tw-justify-center tw-items-center">
                        <Inbox />
                        <span className="tw-text-sm">
                            {t("no-data")}
                        </span>
                    </div>
                )}
            </div>
        </div>
    );
}

export default AddEventFromTemplate;
